import { shallowEqual, useSelector } from 'react-redux'

import PricePanelAmount from '@components/UI/PricePanel/Amount'
import { SupportedSubmissionTypes } from '@enums'
import { IApplicationState } from '@store'
import { ILicenseOption, selectCampaignMinimumLicenseOption } from '@store/campaigns'
import {
  ITransactedLicense,
  selectMediaObjectType,
  selectTransactedLicense,
} from '@store/mediaObjects'
import {
  selectAmountCurrency,
  selectPriceBeforePreApproval,
  selectPriceForDisplay,
  selectSubmission,
  selectTaxDisplayName,
} from '@store/submissions'

interface IProps {
  showLicensePrice: boolean
  showLicenseOptionsPrice: boolean
  submissionId: number
  campaignId: number
  submissionType: SupportedSubmissionTypes
  mediaObjectId: number
}

const getTaxDisplayName = (
  isContentSubmission: boolean,
  showLicensePrice: boolean,
  minimumLicenseOption: ILicenseOption,
  transactedLicense: ITransactedLicense,
  defaultTaxDisplayName: string,
) => {
  if (isContentSubmission) {
    return minimumLicenseOption.tax_display_name
  }
  if (showLicensePrice) {
    return transactedLicense.tax_display_name
  }
  return defaultTaxDisplayName
}

const SubmissionCardPricePanelAmount: React.FC<IProps> = ({
  submissionType,
  showLicensePrice,
  showLicenseOptionsPrice,
  submissionId,
  mediaObjectId,
  campaignId,
}) => {
  const {
    hasPriceChangeSincePreApproval,
    minimumLicenseOption,
    transactedLicense,
    price,
    amountCurrency,
    submissionTaxDisplayName,
  } = useSelector((state: IApplicationState) => {
    const submission = selectSubmission(state, submissionId)
    const mediaObjectMediaType = selectMediaObjectType(state, mediaObjectId)

    const mediaType = submission?.final_media_type ?? mediaObjectMediaType

    return {
      hasPriceChangeSincePreApproval: Boolean(
        selectPriceBeforePreApproval(state, submissionId, campaignId),
      ),
      minimumLicenseOption: selectCampaignMinimumLicenseOption(
        state,
        campaignId,
        mediaType,
      ) as ILicenseOption,
      transactedLicense: selectTransactedLicense(state, mediaObjectId),
      price: selectPriceForDisplay(state, submission.id, campaignId),
      amountCurrency: selectAmountCurrency(state, submissionId),
      submissionTaxDisplayName: selectTaxDisplayName(state, submissionId),
    }
  }, shallowEqual)

  const isContentSubmission = submissionType === SupportedSubmissionTypes.ContentSubmission

  const taxDisplayName = getTaxDisplayName(
    isContentSubmission,
    showLicensePrice,
    minimumLicenseOption,
    transactedLicense,
    submissionTaxDisplayName!,
  )

  if (showLicenseOptionsPrice) {
    return (
      <PricePanelAmount
        showFrom
        price={minimumLicenseOption.total_amount_before_tax_cents}
        currency={minimumLicenseOption.total_amount_before_tax_currency || amountCurrency}
        taxDisplayName={taxDisplayName}
        isInformationAvailable
      />
    )
  }

  if (showLicensePrice) {
    return (
      <PricePanelAmount
        price={transactedLicense.total_amount_before_tax_cents || 0}
        currency={transactedLicense.total_amount_before_tax_currency || amountCurrency}
        taxDisplayName={taxDisplayName}
        isInformationAvailable
      />
    )
  }

  return (
    <PricePanelAmount
      price={price.price || 0}
      currency={price.currency || amountCurrency}
      taxDisplayName={taxDisplayName}
      hasPriceChanged={hasPriceChangeSincePreApproval}
      isInformationAvailable
    />
  )
}

export default SubmissionCardPricePanelAmount
